/*
=========================================================
* RingOperator FAQ — list-form accordion
=========================================================
*/

import { useState } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import { brandPrimary, brandCream, brandAccent } from "pages/RingOperatorHomepage/theme";

const line = "rgba(14,13,11,0.1)";
const muted = "rgba(14,13,11,0.55)";

// Exported so index.js can emit FAQPage JSON-LD from the SAME array that
// renders the visible accordion — see the note in the RingFoods FAQ section.
export const faqs = [
  {
    q: "Does it really sound human?",
    a: "Yes — studio-quality neural voices with natural pacing, laughs, and pauses. Choose from 10 premium AI voices. Most callers never realize they're talking to AI.",
  },
  {
    q: "What happens if the AI can't help?",
    a: "You decide with your custom AI prompts. Transfer the call to a human, send a voicemail summary by SMS or email, and review every call afterward — full transcripts plus audio recordings you can play back. Zero guessing.",
  },
  {
    q: "Can I keep my existing phone number?",
    a: "Yes. Forward your number to RingOperator on no-answer, after-hours, or 24/7. Your number stays on your marketing, website, and business cards.",
  },
  {
    q: "What languages does it support?",
    a: "30+ languages out of the box, including Spanish, Mandarin, French, German, Portuguese, Hindi, Arabic, and Vietnamese. It auto-detects the caller's language.",
  },
  {
    q: "Do I get a webpage for my business?",
    a: "Yes — every plan includes a free public page at ringoperator.com/your-business with your hours, services, and contact info. Customers can book appointments, join your waitlist, and leave feedback right from the page, and you get a printable QR poster for your counter, van, or invoices. It goes live automatically with your phone number.",
  },
  {
    q: "How does the waitlist work?",
    a: "When you're fully booked, customers join the waitlist by phone or from your webpage and can check their live place in line. The moment a slot frees up, the system texts them automatically — and on Growth and Scale plans, the AI can even call them with the opening.",
  },
  {
    q: "Does it connect to my calendar?",
    a: "Yes — native Google Calendar integration. Your availability stays in sync and callers get an instant SMS or email confirmation the moment a booking is made. Holiday closures and special hours are respected automatically.",
  },
  {
    q: "How long does setup take?",
    a: "About 30 minutes. Paste your custom prompt, pick a voice, forward your number. First call gets answered the same day — no setup fees.",
  },
];

function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <MKBox component="section" id="faq" className="ro-root" sx={{ py: { xs: 8, md: 12 }, background: brandCream }}>
      <Container>
        <Grid container spacing={{ xs: 4, md: 8 }}>
          <Grid item xs={12} md={4}>
            <MKBox
              className="ro-mono"
              sx={{
                fontSize: 12,
                color: muted,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                mb: 2,
              }}
            >
              ◦ FAQ
            </MKBox>
            <MKTypography
              component="h2"
              sx={{
                m: 0,
                fontSize: { xs: "2rem", md: "3rem" },
                lineHeight: 0.95,
                letterSpacing: "-0.03em",
                fontWeight: 800,
                color: brandPrimary,
              }}
            >
              Everything you want
              <br />
              <MKBox component="span" className="ro-serif" sx={{ fontStyle: "italic" }}>
                to ask
              </MKBox>{" "}
              before you buy.
            </MKTypography>
            <MKTypography sx={{ color: muted, mt: 3, fontSize: "0.95rem", lineHeight: 1.55 }}>
              Didn't find what you needed?{" "}
              <MKBox
                component="a"
                href="mailto:support@ringoperator.com"
                sx={{ color: brandAccent, textDecoration: "underline" }}
              >
                support@ringoperator.com
              </MKBox>
            </MKTypography>
          </Grid>

          <Grid item xs={12} md={8}>
            <MKBox sx={{ borderTop: `1px solid ${line}` }}>
              {faqs.map((f, i) => {
                const isOpen = open === i;
                return (
                  <MKBox
                    key={i}
                    component="button"
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    sx={{
                      all: "unset",
                      cursor: "pointer",
                      display: "block",
                      width: "100%",
                      borderBottom: `1px solid ${line}`,
                      py: 2.75,
                      transition: "background 180ms ease",
                      "&:hover": { background: "rgba(14,13,11,0.02)" },
                    }}
                  >
                    <MKBox
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      <MKTypography
                        sx={{
                          fontSize: { xs: "1.1rem", md: "1.3rem" },
                          fontWeight: 600,
                          letterSpacing: "-0.01em",
                          color: brandPrimary,
                          textAlign: "left",
                        }}
                      >
                        {f.q}
                      </MKTypography>
                      <MKBox
                        sx={{
                          fontSize: 18,
                          color: isOpen ? brandAccent : muted,
                          transform: isOpen ? "rotate(45deg)" : "none",
                          transition: "transform 180ms ease, color 180ms ease",
                        }}
                      >
                        +
                      </MKBox>
                    </MKBox>
                    {isOpen && (
                      <MKTypography
                        sx={{
                          color: muted,
                          fontSize: "0.98rem",
                          lineHeight: 1.6,
                          mt: 1.5,
                          maxWidth: 620,
                          animation: "ro-drop 260ms ease",
                        }}
                      >
                        {f.a}
                      </MKTypography>
                    )}
                  </MKBox>
                );
              })}
            </MKBox>
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

export default FAQ;
